/**
 * Round duration using division billing settings.
 * Mirrors server-side roundMinutes.js logic.
 */
export function roundMinutes(
  durationMinutes: number,
  incrementMinutes: number = 6,
  minimumMinutes: number = 6
): number {
  if (durationMinutes <= 0) return minimumMinutes
  let billed = Math.ceil(durationMinutes / incrementMinutes) * incrementMinutes
  billed = Math.max(billed, minimumMinutes)
  return billed
}

/**
 * Format minutes as "Xh Ym" display string.
 * e.g., 90 → "1h 30m", 45 → "45m", 120 → "2h"
 */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return "0m"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

/**
 * Convert minutes to decimal hours with 2 decimal places.
 * e.g., 90 → 1.5, 45 → 0.75
 */
export function minutesToDecimalHours(minutes: number): number {
  return parseFloat((minutes / 60).toFixed(2))
}

/**
 * Convert decimal hours to minutes.
 * e.g., 1.5 → 90, 0.75 → 45
 */
export function decimalHoursToMinutes(hours: number): number {
  return Math.round(hours * 60)
}

/**
 * Format decimal hours as display string.
 * e.g., 1.5 → "1.50", 0 → "0.00"
 */
export function formatDecimalHours(hours: number): string {
  return hours.toFixed(2)
}

/**
 * Format currency amount.
 * e.g., 1234.5 → "$1,234.50"
 */
export function formatAmount(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/**
 * Get the Monday of the week containing the given date.
 */
export function getWeekStart(date: Date): string {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) // Monday
  d.setDate(diff)
  return d.toISOString().split("T")[0]
}

/**
 * Get the Sunday of the week containing the given date.
 */
export function getWeekEnd(date: Date): string {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? 0 : 7) // Sunday
  d.setDate(diff)
  return d.toISOString().split("T")[0]
}

/**
 * Generate array of date strings for a week (Mon-Sun).
 */
export function getWeekDates(weekStart: string): string[] {
  const dates: string[] = []
  const start = new Date(weekStart + "T00:00:00")
  for (let i = 0; i < 7; i++) {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    dates.push(d.toISOString().split("T")[0])
  }
  return dates
}

/**
 * Get today's date as YYYY-MM-DD.
 */
export function todayStr(): string {
  return new Date().toISOString().split("T")[0]
}
